// Smart Call detail — Talmia 2.0 replicated JustCall-style call intelligence.
// Consumes GET /api/calls/{id}/detail which merges the raw JC payload with
// (a) our Talmia AI tags, (b) matched customer/person profile via phone,
// (c) recent orders for the matched email.
//
// Layout: 2-col grid.
//   Left  → AI intelligence, transcript, coaching, action items.
//   Right → JustCall meters, customer intel, recent orders, meta.

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import DataAudit from "./_dataAudit";
import LinkedRecords from "./LinkedRecords";
import { RecordHistory } from "./v2/_shared";
import {
  PhoneCall, User as UserIcon, Sparkles, FileText, Info, Wand2, Loader2, Flame,
  ArrowLeft, PlayCircle, Volume2, Timer, Zap, Building2, ShoppingBag, TrendingUp,
  MessageSquare, GraduationCap, ExternalLink, Award, AlertTriangle, Mic,
} from "lucide-react";
import { T2Panel, T2Chip, T2Stat, T2TierChip, T2Label } from "../quotes/_t2";
import { ScoreRing, MeterBar, EnumMeter } from "../calls/_meters";
import CallsFromNumberModal from "../calls/CallsFromNumberModal";
import CallClassifyPanel from "../calls/CallClassifyPanel";
import CallTranscript from "../calls/CallTranscript";
import Person360Overlay from "../../components/Person360Overlay";
import { History } from "lucide-react";

const SENT_TONE = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("positive")) return "ok";
  if (s.includes("negative")) return "red";
  return "blue";
};
const PRIO_TONE = (v) => v === "High" ? "red" : v === "Medium" ? "warn" : "";
const CAT_TONE  = () => "violet";

const GBP = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `£${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
};
const fmtDur = (s) => {
  const n = Number(s || 0);
  if (!n) return "—";
  const m = Math.floor(n/60), r = n % 60;
  return `${m}m ${String(r).padStart(2, "0")}s`;
};
const dateGB = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-GB"); } catch { return String(v); }
};

/* Peel a JustCall AI number field like "58%" → 58; "0.58" → 58 */
const pctOf = (v) => {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace("%", "");
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n <= 1 ? Math.round(n * 100) : Math.round(n);
};

/* Try to derive a recording URL from every place JustCall might put it. */
function pickRecordingUrl(jc) {
  return (
    jc?.recording_url
    || jc?.recording?.url
    || jc?.call_recording
    || jc?.recording_urls?.[0]
    || null
  );
}

export default function LabCallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tagging, setTagging] = useState(false);
  const [showNumberModal, setShowNumberModal] = useState(false);
  const [show360, setShow360] = useState(false);

  const reload = async () => {
    const { data } = await api.get(`/calls/${id}/detail`);
    setPayload(data);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/calls/${id}/detail`);
        if (alive) setPayload(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load call");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const runTag = async () => {
    setTagging(true);
    try {
      const { data } = await api.post(`/calls/${id}/tag`);
      toast.success(`AI tagged · ${data.tags?.category} · ${data.tags?.sentiment} · Score ${data.tags?.score?.overall ?? "—"}`);
      await reload();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Tagging failed"));
    } finally { setTagging(false); }
  };

  const doc = payload?.call;
  const matched = payload?.matched_contact;
  const orders = payload?.recent_orders || [];

  const jcData = useMemo(() => {
    const jc = ((doc?.JCCall || {}).data) || {};
    return Array.isArray(jc) ? (jc[0] || {}) : jc;
  }, [doc]);

  if (loading) return <Layout><div style={{ textAlign: "center", padding: 80, color: "var(--t2-grey3)" }}>Loading call…</div></Layout>;
  if (err || !doc) return <Layout><div style={{ textAlign: "center", padding: 80, color: "var(--t2-red)" }}>{err || "Call not found"}</div></Layout>;

  const info = jcData.call_info || {};
  const dur = jcData.call_duration || {};
  const ivr = jcData.ivr_info || {};
  const jcAi = jcData.justcall_ai || {};
  const tags = doc.AITags || null;
  const score = tags?.score || null;
  const recordingUrl = pickRecordingUrl(jcData);
  const totalDur = Number(dur.total_duration) || 0;

  const talkPctCust = pctOf(jcAi.talk_ratio_customer ?? jcAi.customer_talk_ratio);
  const talkPctAgent = pctOf(jcAi.talk_ratio ?? jcAi.agent_talk_ratio);
  const silencePct = pctOf(jcAi.silence_percentage);
  const derivedAgent = talkPctAgent ?? (talkPctCust != null ? 100 - talkPctCust : null);
  const derivedCust  = talkPctCust  ?? (talkPctAgent != null ? 100 - talkPctAgent : null);

  const contactName = matched?.name || jcData.contact_name || jcData.contact_number || "Unknown caller";

  return (
    <Layout>
      <div data-testid="smart-call-page">
        {/* Breadcrumb */}
        <button onClick={() => navigate("/calls")} className="t2-btn-ghost"
          style={{ marginBottom: 10, fontSize: 11 }} data-testid="back-to-calls">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Calls
        </button>
        {!matched?.personId && doc?.MatchedPersonId && (
          <button type="button" onClick={() => setShow360(true)} className="t2-btn-primary" style={{ marginBottom: 10, marginLeft: 8, fontSize: 11 }} data-testid="call-open-360">
            Customer 360
          </button>
        )}

        {/* ─── ABOVE-THE-FOLD BAND ─────────────────────────────────────────
            Three-column strip that shows the entire call's story at a glance:
            (1) Score + meters + chip identity
            (2) AI intelligence — summary, meters, next step, top chips
            (3) Customer intel — matched profile, LTV, deal, notes preview
            Everything else (transcript, coaching, actions, meta) sits below.
            ────────────────────────────────────────────────────────────── */}
        <div className="lcd-grid-top" style={{
          display: "grid",
          gridTemplateColumns: "minmax(300px, 320px) 1fr minmax(280px, 320px)",
          gap: 14, marginBottom: 14,
        }}>

          {/* ── Col 1 · Score + Identity ───────────────────────────────── */}
          <T2Panel testid="hero-panel" padded={false} title={null}>
            <div style={{ padding: "16px 16px 14px" }}>
              {/* Score ring + band + call number/agent identity */}
              <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 12 }}>
                <ScoreRing value={score?.overall ?? 0} label={score?.band || "Not scored"} size={94} testid="hero-score" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-brass)", fontWeight: 700 }}>
                    {jcData.contact_number || "—"}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "var(--t2-white)", lineHeight: 1.2, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {contactName}
                  </div>
                  <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey3)", marginTop: 4 }}>
                    <Timer className="w-2.5 h-2.5 inline-block" /> {fmtDur(totalDur)}
                    {info.direction && <> · {info.direction}</>}
                  </div>
                  <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)", marginTop: 2 }}>
                    {jcData.agent_name || "—"}
                    {jcData.justcall_line_name && <> · {jcData.justcall_line_name}</>}
                  </div>
                  <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey4)", marginTop: 2 }}>
                    {dateGB(doc.CreatedDate)}
                  </div>
                  {jcData.contact_number && (
                    <button
                      data-testid="call-view-all-from-number"
                      onClick={() => setShowNumberModal(true)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8,
                        background: "var(--t2-orange-dim)", border: "1px solid var(--t2-orange-glow)",
                        color: "var(--t2-orange)", borderRadius: 7, padding: "5px 10px",
                        fontSize: 10.5, fontWeight: 700, cursor: "pointer",
                      }} className="t2-mono">
                      <History className="w-3 h-3" /> View all calls from this number
                    </button>
                  )}
                </div>
              </div>

              {/* Chip strip */}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
                {tags?.category && <T2Chip tone={CAT_TONE()}>{tags.category}</T2Chip>}
                {tags?.sentiment && <T2Chip tone={SENT_TONE(tags.sentiment)}>{tags.sentiment}</T2Chip>}
                {tags?.priority && <T2Chip tone={PRIO_TONE(tags.priority)}>{tags.priority}</T2Chip>}
                {matched?.tier && <T2TierChip tier={matched.tier} />}
                {doc.IsParked && <T2Chip tone="warn">Parked</T2Chip>}
              </div>

              {/* Score meter bars */}
              {score && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <MeterBar label="Communication"   value={score.communication} max={25} tone="orange" suffix="/25" testid="score-comm" />
                  <MeterBar label="Brand adherence" value={score.brand}         max={25} tone="teal"   suffix="/25" testid="score-brand" />
                  <MeterBar label="Sentiment"       value={score.sentiment}     max={25} tone="brass"  suffix="/25" testid="score-sent" />
                  <MeterBar label="Etiquette"       value={score.etiquette}     max={25} tone="violet" suffix="/25" testid="score-etiquette" />
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                <button onClick={runTag} disabled={tagging} className="t2-btn-primary"
                  style={{ flex: 1 }} data-testid="btn-analyse">
                  {tagging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {tags ? "Re-analyse" : "Analyse"}
                </button>
                {recordingUrl ? (
                  <a href={recordingUrl} target="_blank" rel="noreferrer"
                    className="t2-btn-ghost" style={{ flex: 1, justifyContent: "center", fontSize: 10.5 }}
                    data-testid="btn-recording">
                    <PlayCircle className="w-3.5 h-3.5" /> Listen
                  </a>
                ) : (
                  <div className="t2-mono" style={{ flex: 1, textAlign: "center",
                    fontSize: 9, color: "var(--t2-grey4)", alignSelf: "center",
                    padding: "6px 8px", border: "1px dashed var(--t2-border)", borderRadius: 8 }}>
                    <Mic className="w-3 h-3 inline-block" /> No recording
                  </div>
                )}
              </div>
            </div>
          </T2Panel>

          {/* ── Col 2 · AI Intelligence (compact) ──────────────────────── */}
          <T2Panel icon={Wand2} title="Talmia AI · Intelligence"
            right={tags ? <span className="t2-mono">Model · {tags.tagged_by}</span> : <span>Not yet analysed</span>}
            testid="panel-ai-intel">
            {!tags && !tagging && (
              <div style={{ fontSize: 13, color: "var(--t2-grey2)", padding: "16px 4px" }}>
                Click <b style={{ color: "var(--t2-orange)" }}>Analyse</b> to extract category, sentiment,
                priority, topics, coaching notes and a Talmia Call Score.
              </div>
            )}
            {tagging && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--t2-grey1)", padding: 12 }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--t2-orange)" }} />
                <span style={{ fontSize: 13 }}>Gemini 3 Flash is analysing…</span>
              </div>
            )}
            {tags && (
              <div>
                {tags.summary && (
                  <div style={{ fontSize: 13, color: "var(--t2-white)", lineHeight: 1.4, marginBottom: 10 }}>
                    &ldquo;{tags.summary}&rdquo;
                  </div>
                )}
                {/* Enum meters, 2x2 grid, small */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <EnumMeter label="Probing"    value={tags.probing_quality}
                    options={["Weak", "OK", "Strong"]}
                    toneMap={{ Weak: "red", OK: "warn", Strong: "ok" }}
                    testid="meter-probing" />
                  <EnumMeter label="Empathy"    value={tags.agent_empathy}
                    options={["Low", "Adequate", "Strong"]}
                    toneMap={{ Low: "red", Adequate: "warn", Strong: "ok" }}
                    testid="meter-empathy" />
                  <EnumMeter label="Adherence"  value={tags.script_adherence}
                    options={["Off-script", "Partial", "Strong"]}
                    toneMap={{ "Off-script": "red", Partial: "warn", Strong: "ok" }}
                    testid="meter-script" />
                  <EnumMeter label="Intent"     value={tags.customer_intent}
                    options={["Browse", "Compare", "Ready to buy"]}
                    toneMap={{ "Ready to buy": "ok", "Compare": "orange", "Browse": "" }}
                    testid="meter-intent" />
                </div>

                {/* Compressed chip clusters — only what fits */}
                {(tags.topics?.length > 0 || tags.build_types?.length > 0) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                    {(tags.topics || []).slice(0, 4).map((t) => <T2Chip key={`t-${t}`} tone="brass">{t}</T2Chip>)}
                    {(tags.build_types || []).slice(0, 3).map((b) => <T2Chip key={`b-${b}`} tone="teal">{b}</T2Chip>)}
                    {(tags.products_mentioned || []).slice(0, 3).map((p) => <T2Chip key={`p-${p}`} tone="orange">{p}</T2Chip>)}
                    {(tags.competitors_mentioned || []).slice(0, 3).map((c) => <T2Chip key={`c-${c}`} tone="red">{c}</T2Chip>)}
                    {(tags.objections_raised || []).slice(0, 3).map((o) => <T2Chip key={`o-${o}`} tone="warn">{o}</T2Chip>)}
                  </div>
                )}

                {/* Next step */}
                {tags.outbound_next_step && (
                  <div style={{
                    padding: "8px 12px",
                    background: "var(--t2-orange-dim)",
                    border: "1px solid var(--t2-orange-glow)",
                    borderRadius: 8,
                    display: "flex", alignItems: "center", gap: 8,
                    marginBottom: 8,
                  }}>
                    <Flame className="w-3.5 h-3.5" style={{ color: "var(--t2-orange)", flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div className="t2-mono" style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--t2-orange)", textTransform: "uppercase", fontWeight: 700 }}>Next step</div>
                      <div style={{ fontSize: 12.5, color: "var(--t2-white)", fontWeight: 600, lineHeight: 1.3 }}>{tags.outbound_next_step}</div>
                    </div>
                  </div>
                )}

                {/* Top 2 action items inline */}
                {tags.action_items?.length > 0 && (
                  <div>
                    <T2Label>Action items</T2Label>
                    <ul style={{ listStyle: "disc", paddingLeft: 18, fontSize: 12, color: "var(--t2-grey1)", lineHeight: 1.5, margin: 0 }}>
                      {tags.action_items.slice(0, 3).map((a, i) => <li key={`ai-${i}-${String(a).slice(0, 24)}`}>{a}</li>)}
                      {tags.action_items.length > 3 && (
                        <li className="t2-mono" style={{ listStyle: "none", color: "var(--t2-grey4)", fontSize: 10, marginTop: 2 }}>
                          +{tags.action_items.length - 3} more below
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </T2Panel>

          {/* ── Col 3 · Customer summary ───────────────────────────────── */}
          <T2Panel icon={Building2} title="Customer summary"
            right={matched?.hasIdentity ? <T2Chip tone="ok">Matched</T2Chip> : <T2Chip>No match</T2Chip>}
            testid="panel-customer">
            {!matched?.hasIdentity ? (
              <div style={{ fontSize: 12, color: "var(--t2-grey3)", lineHeight: 1.5 }}>
                The phone <b className="t2-mono" style={{ color: "var(--t2-brass)" }}>{jcData.contact_number || "—"}</b> doesn&apos;t match any customer or Pipedrive person yet.
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 14, color: "var(--t2-white)", fontWeight: 800, lineHeight: 1.3 }}>{matched.name}</div>
                {matched.company && <div style={{ fontSize: 11, color: "var(--t2-grey2)", marginTop: 2 }}>{matched.company}</div>}
                {matched.email && <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-orange)", marginTop: 3, wordBreak: "break-all" }}>{matched.email}</div>}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5, marginTop: 10 }}>
                  <T2Stat label="LTV"    value={GBP(matched.ltv)} />
                  <T2Stat label="Orders" value={matched.orders ?? "—"} />
                  <T2Stat label="Avg"    value={GBP(matched.avgOrder)} />
                </div>

                {/* Wave M: `dealTitle` display removed — DealId/DealTitle
                    are ignored per user directive. */}

                {matched.lastOrderDate && (
                  <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey3)", marginTop: 8 }}>
                    Last order · {new Date(matched.lastOrderDate).toLocaleDateString("en-GB")}
                  </div>
                )}

                {/* Notes preview — from person/customer records */}
                {matched.notes && (
                  <div style={{ marginTop: 10, padding: "8px 10px", background: "var(--t2-surface2)", border: "1px solid var(--t2-border)", borderRadius: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3, color: "var(--t2-teal)" }}>
                      <MessageSquare className="w-3 h-3" />
                      <span className="t2-mono" style={{ fontSize: 9, letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 700 }}>Notes on file</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--t2-grey1)", lineHeight: 1.45, whiteSpace: "pre-wrap",
                      maxHeight: 76, overflow: "hidden", textOverflow: "ellipsis",
                    }}>{matched.notes}</div>
                  </div>
                )}

                {/* Recent orders — compressed rows */}
                {orders.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <T2Label>Recent orders · {orders.length}</T2Label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {orders.slice(0, 3).map((o) => (
                        <Link key={o.orderId} to={`/lab/orders/${o.orderId}`} style={{
                          display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 6, alignItems: "center",
                          padding: "4px 6px", borderRadius: 5,
                          background: "var(--t2-surface2)", border: "1px solid var(--t2-border)",
                          textDecoration: "none",
                        }}>
                          <span className="t2-mono" style={{ color: "var(--t2-orange)", fontSize: 10 }}>#{o.orderId}</span>
                          <span className="t2-mono" style={{ color: "var(--t2-grey3)", fontSize: 9.5 }}>
                            {o.date ? new Date(o.date).toLocaleDateString("en-GB") : "—"}
                            {o.status && <> · {o.status}</>}
                          </span>
                          <span className="t2-mono" style={{ color: "var(--t2-white)", fontSize: 11, fontWeight: 700 }}>
                            {GBP(o.totalInc || o.totalEx)}
                          </span>
                        </Link>
                      ))}
                      {orders.length > 3 && (
                        <span className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey4)", padding: "2px 6px" }}>
                          +{orders.length - 3} more below
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 4, marginTop: 10, flexWrap: "wrap" }}>
                  {matched.personId && (
                    <button type="button" onClick={() => setShow360(true)} className="t2-btn-primary" style={{ flex: 1, fontSize: 10 }} data-testid="call-open-360">
                      360 view
                    </button>
                  )}
                  {matched.personId && (
                    <Link to={`/lab/persons/${matched.personId}`} className="t2-btn-ghost" style={{ flex: 1, fontSize: 10 }}>
                      <UserIcon className="w-3 h-3" /> Person <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  )}
                  {matched.customerId && (
                    <Link to={`/lab/customers/${matched.customerId}`} className="t2-btn-ghost" style={{ flex: 1, fontSize: 10 }}>
                      <Building2 className="w-3 h-3" /> Customer <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  )}
                </div>
              </div>
            )}
          </T2Panel>
        </div>

        {/* ─── BELOW-THE-FOLD DETAIL ROW ────────────────────────────────
            Coaching, JC meters, transcript, full action items, meta, raw JC.
            ─────────────────────────────────────────────────────────── */}
        <div className="lcd-grid-main" style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 14 }}>

          {/* LEFT — coaching + transcript + actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Coaching */}
            {tags && (tags.coaching_strengths?.length > 0 || tags.coaching_notes?.length > 0) && (
              <T2Panel icon={GraduationCap} title="Coaching feedback" testid="panel-coaching">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, color: "var(--t2-ok)" }}>
                      <Award className="w-3.5 h-3.5" />
                      <span className="t2-mono" style={{ fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 700 }}>Strengths</span>
                    </div>
                    {(tags.coaching_strengths || []).length === 0 ? (
                      <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey4)" }}>—</div>
                    ) : (
                      <ul style={{ listStyle: "disc", paddingLeft: 18, fontSize: 12.5, color: "var(--t2-grey1)", lineHeight: 1.5 }}>
                        {(tags.coaching_strengths || []).map((s, i) => <li key={`cs-${i}-${String(s).slice(0, 24)}`}>{s}</li>)}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, color: "var(--t2-warn)" }}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span className="t2-mono" style={{ fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 700 }}>Improve</span>
                    </div>
                    {(tags.coaching_notes || []).length === 0 ? (
                      <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey4)" }}>—</div>
                    ) : (
                      <ul style={{ listStyle: "disc", paddingLeft: 18, fontSize: 12.5, color: "var(--t2-grey1)", lineHeight: 1.5 }}>
                        {(tags.coaching_notes || []).map((s, i) => <li key={`cn-${i}-${String(s).slice(0, 24)}`}>{s}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              </T2Panel>
            )}

            {/* Full action items list */}
            {tags?.action_items?.length > 0 && (
              <T2Panel icon={Zap} title="All action items" right={<span>{tags.action_items.length}</span>} testid="panel-actions">
                <ul style={{ listStyle: "disc", paddingLeft: 18, fontSize: 13, color: "var(--t2-white)", lineHeight: 1.6 }}>
                  {tags.action_items.map((a, i) => <li key={`ai-full-${i}-${String(a).slice(0, 24)}`}>{a}</li>)}
                </ul>
              </T2Panel>
            )}

            <CallClassifyPanel doc={doc} onSaved={reload} />
            <CallTranscript doc={doc} jcData={jcData} jcAi={jcAi} info={info} onReload={reload} />
          </div>

          {/* RIGHT — JC meters + call meta + raw JC AI */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <T2Panel icon={Volume2} title="JustCall meters" testid="panel-jc-meters">
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {derivedAgent != null && (
                  <MeterBar label="Agent talk %" value={derivedAgent} max={100} tone="teal" suffix="%" testid="meter-talk-agent" />
                )}
                {derivedCust != null && (
                  <MeterBar label="Customer talk %" value={derivedCust} max={100} tone="orange" suffix="%" testid="meter-talk-cust" />
                )}
                {silencePct != null && (
                  <MeterBar label="Silence" value={silencePct} max={100} tone="warn" suffix="%" testid="meter-silence" />
                )}
                {tags?.filler_word_count != null && (
                  <MeterBar label="Filler words" value={tags.filler_word_count} max={30} tone="red" testid="meter-filler" />
                )}
                {(derivedAgent == null && silencePct == null && tags?.filler_word_count == null) && (
                  <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey4)" }}>
                    Talk-ratio / silence come straight from JustCall when the payload includes them.
                  </div>
                )}
              </div>
            </T2Panel>

            <T2Panel icon={Info} title="Call meta" testid="panel-meta">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div><T2Label>Type</T2Label><div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-grey1)" }}>{info.type || "—"}</div></div>
                <div><T2Label>Status</T2Label><div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-grey1)" }}>{info.status || info.disposition || "—"}</div></div>
                <div><T2Label>Recorded</T2Label><div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey1)" }}>{jcData.call_date} {jcData.call_time}</div></div>
                <div><T2Label>Duration</T2Label><div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-white)", fontWeight: 700 }}>{fmtDur(totalDur)}</div></div>
                {ivr.content && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <T2Label>IVR path</T2Label>
                    <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-teal)" }}>
                      Pressed {ivr.digit_pressed} → {ivr.content}
                    </div>
                  </div>
                )}
              </div>
            </T2Panel>

            {jcAi.customer_sentiment && (
              <T2Panel icon={TrendingUp} title="JustCall AI (raw)" testid="panel-jc-raw">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div><T2Label>Customer</T2Label><div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-grey1)" }}>{jcAi.customer_sentiment}</div></div>
                  {jcAi.agent_sentiment && (
                    <div><T2Label>Agent</T2Label><div className="t2-mono" style={{ fontSize: 12, color: "var(--t2-grey1)" }}>{jcAi.agent_sentiment}</div></div>
                  )}
                </div>
                {jcAi.summary && (
                  <div style={{ marginTop: 10 }}>
                    <T2Label>Raw summary</T2Label>
                    <div style={{ fontSize: 12, color: "var(--t2-grey1)", lineHeight: 1.5 }}>{jcAi.summary}</div>
                  </div>
                )}
              </T2Panel>
            )}
          </div>
        </div>
      </div>
    
      {show360 && (matched?.personId || doc?.MatchedPersonId) && <Person360Overlay personId={matched?.personId || doc.MatchedPersonId} onClose={() => setShow360(false)} />}
      <LinkedRecords collection="calls" id={id} />
      <DataAudit doc={doc} title="Data audit — full record" testid="lab-data-audit" />
      <RecordHistory collection="calls" docId={id} testid="lab-call-history" />
      {showNumberModal && jcData.contact_number && (
        <CallsFromNumberModal number={jcData.contact_number} onClose={() => setShowNumberModal(false)} />
      )}
    </Layout>
  );
}
